import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';

interface ReAuthModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function ReAuthModal({ open, onClose, onSuccess }: ReAuthModalProps) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;

    const fetchEmail = async () => {
      const { data } = await supabase.auth.getUser();
      if (data?.user?.email) {
        setAdminEmail(data.user.email);
      }
    };
    fetchEmail();

    setPassword('');
    setError('');

    // Focus input after modal animation
    setTimeout(() => {
      inputRef.current?.focus();
    }, 150);
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!adminEmail) {
      setError('사용자 정보를 불러올 수 없습니다. 다시 로그인해주세요.');
      return;
    }
    if (!password.trim()) {
      setError('비밀번호를 입력해주세요.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: adminEmail,
        password,
      });

      if (signInError) {
        setError('비밀번호가 올바르지 않습니다.');
      } else {
        onSuccess();
        onClose();
      }
    } catch {
      setError('인증 중 오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-background-50 rounded-2xl w-full max-w-sm mx-4 shadow-lg">
        {/* Header */}
        <div className="flex items-center gap-3 p-5 border-b border-background-200">
          <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center">
            <i className="ri-shield-keyhole-line text-lg text-accent-600"></i>
          </div>
          <div>
            <h3 className="font-heading text-base text-foreground-800">관리자 재인증</h3>
            <p className="text-xs text-foreground-400">민감 정보 조회를 위해 비밀번호를 다시 입력해주세요.</p>
          </div>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-5">
          <div className="mb-1">
            <label className="block text-xs text-foreground-500 mb-1.5">관리자 계정</label>
            <p className="text-sm text-foreground-700 bg-background-50 rounded-lg px-3 py-2.5 font-mono">
              {adminEmail || '불러오는 중...'}
            </p>
          </div>

          <div className="mt-4">
            <label htmlFor="reauth-password" className="block text-xs text-foreground-500 mb-1.5">
              비밀번호
            </label>
            <div className="relative">
              <i className="ri-lock-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
              <input
                ref={inputRef}
                id="reauth-password"
                type="password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(''); }}
                placeholder="비밀번호 입력"
                className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-background-300 text-sm text-foreground-800 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400 transition-shadow"
                autoComplete="current-password"
              />
            </div>
          </div>

          {error && (
            <div className="mt-3 flex items-center gap-1.5 text-xs text-accent-600 bg-accent-50 rounded-lg px-3 py-2">
              <i className="ri-error-warning-line"></i>
              {error}
            </div>
          )}

          <div className="flex gap-2 mt-5">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 rounded-lg border border-background-300 text-sm text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              취소
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2.5 rounded-lg bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
            >
              {loading ? (
                <>
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                  인증 중...
                </>
              ) : (
                <>
                  <i className="ri-shield-check-line"></i>
                  인증 확인
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}